// Skill descriptors for the slash-menu in Composer.
// Source of truth: the 57 skills living on the Hetzner agent box at
// /home/claude/.claude/skills/. Agents edit them THERE; scripts/skills/ in
// this repo is a backup that is pulled box→repo. Never push repo→box.
//
// If the user types a `/foo` not in this list, the message still goes
// through — the popup is purely an autocomplete helper.

export interface SkillDescriptor {
  /** Slash command (no leading slash). */
  name: string;
  /** Short one-line description shown in the popup. */
  summary: string;
  /** Grouping label for visual separation in the popup. */
  group: SkillGroup;
}

export type SkillGroup =
  | "Built-in"
  | "Notifications"
  | "Client work"
  | "Documents"
  | "Creative"
  | "Integrations"
  | "Admin"
  | "Meta";

export const SKILLS: SkillDescriptor[] = [
  // Claude Code built-ins — intercepted by the CLI input parser,
  // never reach the model. Listed first so they're the most reachable.
  // /compact never goes to the model: the composer runs native Compact (the
  // same rail as the context meter's button).
  { name: "compact",         summary: "Summarise this chat into a shorter context (keeps the same chat)", group: "Built-in" },
  { name: "goal",            summary: "Set a persistent goal for this session (Claude focuses on it)", group: "Built-in" },
  { name: "clear",           summary: "Start a fresh conversation (same client, empty context)",       group: "Built-in" },
  { name: "cost",            summary: "Show current Anthropic usage for the session",                  group: "Built-in" },

  // Notifications — /notify-julian is meta (handled client-side; sets a flag so
  // the backend pings Julian's Telegram on this turn's done). /notify-farhan and
  // /notify-all insert text → the agent runs the notify-team box skill.
  { name: "notify-julian",   summary: "Ping ME (Julian) on Telegram when this turn finishes",           group: "Notifications" },
  { name: "notify-farhan",   summary: "Ping Farhan on Telegram — use after a sample deploys (send the live link)", group: "Notifications" },
  { name: "notify-all",      summary: "Ping BOTH Julian + Farhan on Telegram",                          group: "Notifications" },

  // Client website work
  { name: "verify-page",     summary: "Screenshot a client site (desktop + mobile) and visually verify changes via Claude vision", group: "Client work" },
  { name: "sample",          summary: "Turn a lead's WhatsApp chat into a paste-ready, bespoke website build prompt", group: "Client work" },
  { name: "codex-delegate-sample-creative", summary: "Creative sample build with Astra — automatic thinking, in Samples only", group: "Client work" },
  { name: "opus-sample-creative", summary: "Opus 5.5 creative sample: real Awwwards/Framer research → traced design → deploy", group: "Client work" },
  { name: "recycle-sample", summary: "Copy a finished site for this lead: type @ to tag it, only the brand details change", group: "Client work" },
  { name: "enhance",         summary: "Elevate the current design with the design gallery — paste EXACT CSS + motion from bought templates (opt-in; separate from /sample)", group: "Client work" },
  { name: "deploy-site",     summary: "Deploy a new client site from a brief or homepage scrape",     group: "Client work" },
  { name: "revision",        summary: "Generate a /revision link — agent builds the scope from the live site", group: "Client work" },
  { name: "revisions",       summary: "Set up the INBUILT client portal — on-domain login + one-time magic link (binds a /revision scope)", group: "Client work" },
  { name: "ideas",           summary: "Build upsell proposals on the client's own site + show them in their portal for one-tap approval (Now/Proposed preview; needs plugin ≥1.92.3)", group: "Client work" },
  { name: "setup-chatbot",   summary: "Install the Reputifly chatbot widget on a client site",        group: "Client work" },
  { name: "blog-setup",      summary: "Wire blog templates + first post on the client site",          group: "Client work" },
  { name: "polish-analyze",  summary: "Audit the live client site against the polish handbook + fix what fails", group: "Client work" },
  { name: "liquid-glass",    summary: "Apply Apple's exact Liquid Glass (iOS 26 / Tahoe) material to cards, navs, buttons", group: "Client work" },
  { name: "deep-audit",      summary: "Ruthless audit of a live site against the client's revisions — parallel Opus auditors + fix sheet", group: "Client work" },
  { name: "scrape",          summary: "Pull a lead's PUBLIC Instagram (photos + captions + logo) to personalise a sample — Facebook is best-effort from the box", group: "Client work" },

  // PDFs (host on the client's own WordPress media library)
  { name: "scope",           summary: "Generate a scope-of-work PDF",                                  group: "Documents" },
  { name: "agreement",       summary: "Generate a service agreement PDF",                              group: "Documents" },
  { name: "quotation",       summary: "Generate a price quotation PDF",                                group: "Documents" },
  { name: "client-proposal", summary: "Client Proposal — confirm hosting and page scope before sending", group: "Documents" },
  { name: "ad-proposal",     summary: "Generate an ad campaign proposal PDF",                          group: "Documents" },
  { name: "god-audit",       summary: "Comprehensive 25-40 page website audit PDF (the agency killer)", group: "Documents" },

  // Creative / images
  { name: "gen-image",       summary: "Generate an image via Gemini Nano Banana 2",                    group: "Creative" },
  { name: "gen-image-codex", summary: "Generate an image via Codex / gpt-image-2 on the ChatGPT plan (free at the margin, no API $)", group: "Creative" },
  { name: "post-creative",   summary: "Build a social-media post creative (single / carousel)",        group: "Creative" },
  { name: "ad-creative",     summary: "Build an ad creative for Meta / Google",                        group: "Creative" },

  // Integrations
  { name: "nexus",           summary: "Configure form-to-email Nexus integration",                    group: "Integrations" },
  { name: "email",           summary: "Notify someone by email (e.g. sample deployed) — give an address, agent emails them; no per-person setup", group: "Integrations" },
  { name: "drive",           summary: "Branded file collect/deliver links on drive.reputifly.cloud",   group: "Integrations" },
  { name: "images",          summary: "Build a per-page/per-section Drive tree for the client to upload photos + video", group: "Integrations" },

  // Admin
  { name: "cloud-control",   summary: "Fleet control via the universal cloud key — list / snapshot / duplicate / spin up sites + drop into any client", group: "Admin" },
  { name: "register",        summary: "Register a new client by pasting their curl command",          group: "Admin" },
  { name: "update-client",   summary: "Rotate the agent key for an existing client",                  group: "Admin" },
  { name: "unregister",      summary: "Remove a client from the registry (keeps backup)",             group: "Admin" },
  { name: "companies",       summary: "List every registered client",                                  group: "Admin" },
  { name: "changelog",       summary: "Append an entry to the agency changelog",                      group: "Admin" },
  { name: "guide",           summary: "Show the slash-command quick reference",                       group: "Admin" },

  // Meta / tooling
  { name: "polish-add",      summary: "Capture UI/UX polish feedback into the handbook (writes rules only, never a site)", group: "Meta" },
  { name: "skill-setup",     summary: "Bootstrap a new custom skill",                                 group: "Meta" },
  { name: "reputifly-styles",summary: "Apply Reputifly brand styles audit to a site",                 group: "Meta" },
  { name: "taskforce",       summary: "Spin up a multi-agent task force for a complex job",           group: "Meta" },
];
// NOTE: both polish skills are in the menu now. /polish-analyze audits a client's
// live site (the box has a live-URL port). /polish-add captures polish RULES into
// the handbook — CAVEAT: on the box that writes ~/Desktop/Websites/_polish/, a
// one-way rsync TARGET from the Mac, so box-side edits are overwritten on the next
// Mac→box handbook sync; capture polish on the Mac (or sync back) to persist.

/**
 * Fuzzy filter the skill list against a query (typed after the slash).
 * Returns skills whose name starts-with query first, then includes-query,
 * preserving SKILLS ordering within each tier.
 */
// ── LIVE SKILL LIST (2026-08-04) ─────────────────────────────────────────────
// The array above is now only a FALLBACK. It is a hand-maintained mirror of a
// directory, so it always drifts: measured 11 skills live on the box that never
// appeared in this popup — including /gen-video, which is exactly why Julian
// typed it and got nothing. The real list comes from GET /api/skills, which
// reads /home/claude/.claude/skills/ on the box.
let liveSkills: SkillDescriptor[] | null = null;

/** Merge the box's real skills over the static list (box wins on name clash). */
function mergedSkills(): SkillDescriptor[] {
  if (!liveSkills || liveSkills.length === 0) return SKILLS;
  const byName = new Map<string, SkillDescriptor>();
  // keep the built-ins + client-side-only commands that have no SKILL.md
  for (const s of SKILLS) {
    if (s.group === "Built-in" || s.group === "Notifications") byName.set(s.name, s);
  }
  for (const s of liveSkills) {
    const known = SKILLS.find((candidate) => candidate.name === s.name);
    byName.set(s.name, {
      ...s,
      summary:
        s.name === "client-proposal"
          ? known?.summary ?? s.summary
          : s.summary || known?.summary || "",
      group: known?.group ?? s.group,
    });
  }
  // anything static we could not confirm on the box still stays reachable
  for (const s of SKILLS) if (!byName.has(s.name)) byName.set(s.name, s);
  return [...byName.values()];
}

/** Called once at app start. Never throws — a failed fetch just keeps SKILLS. */
export async function loadLiveSkills(
  fetcher: (path: string) => Promise<Response>,
): Promise<number> {
  try {
    const r = await fetcher("/api/skills");
    if (!r.ok) return 0;
    const j = (await r.json()) as { skills?: { name: string; summary: string }[] };
    if (!Array.isArray(j.skills) || j.skills.length === 0) return 0;
    liveSkills = j.skills.map((s) => ({
      name: s.name,
      summary: s.summary || "",
      group: "Client work" as SkillGroup,
    }));
    return liveSkills.length;
  } catch {
    return 0; // offline / auth blip → fallback list, popup still works
  }
}

/**
 * How well `name` matches `q`. LOWER is better; -1 means no match at all.
 *
 *   0  exact              /revisions        → revisions
 *   1  prefix             /rev              → revision, revisions, revision-v3
 *   2  segment start      /client           → update-client   (after a "-")
 *   3  anywhere else      /age              → verify-page
 *
 * Matching is on the NAME only, exactly as before — this ranks the same set of
 * results, it does not widen it.
 */
function matchRank(name: string, q: string): number {
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  const at = name.indexOf(q);
  if (at < 0) return -1;
  return name[at - 1] === "-" ? 2 : 3;
}

export function filterSkills(query: string): SkillDescriptor[] {
  const all = mergedSkills();
  const q = query.trim().toLowerCase();
  // No query = the browse list. Left in its curated order (built-ins first, then
  // grouped) so the popup still reads as a menu rather than a ranked search.
  if (!q) return all;

  // Rank, don't just bucket. The old two-tier split preserved merged-list order
  // inside each tier, and that order is "live skills alphabetically, then
  // whatever only exists in the static list". So `/rev` put `revision-v3` first
  // and pushed plain `revision` to the very end of its own tier — the exact
  // thing you had to scroll past.
  return all
    .map((s, i) => ({ s, i, rank: matchRank(s.name.toLowerCase(), q) }))
    .filter((e) => e.rank >= 0)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        // Same quality of match: the shorter name is the more specific one,
        // so /rev → revision before revisions before revision-v3.
        a.s.name.length - b.s.name.length ||
        // Fully tied: keep the list's own order so results never jump around.
        a.i - b.i,
    )
    .map((e) => e.s);
}
